import { createContext, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode"; // Correct named import
import apiService from "../utils/apiService";
import Spinner from "../Spinner";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loginType, setLoginType] = useState(localStorage.getItem("loginType") || ""); // Store loginType in context
  const [loading, setLoading] = useState(true); // Start with loading state
  const [permissions, setPermissions] = useState([]);
  const navigate = useNavigate();

  // Decode JWT token and check expiry
  const isTokenExpired = (token) => {
    try {
      const decoded = jwtDecode(token); // Use jwtDecode
      const currentTime = Date.now() / 1000; // Current time in seconds
      return decoded.exp < currentTime;
    } catch (error) {
      console.error("Token decoding failed:", error);
      return true; // Treat token as expired if decoding fails
    }
  };

  const setErrorMessage = (message) => {
    console.error(message);
    setLoading(false);
  };

  // Validate token and handle expiry
  const validateToken = async (token) => {
    if (isTokenExpired(token)) {
      console.log("Token is expired");
      handleLogout();
      return false;
    }
    try {
      const response = await apiService.post("auth/validate-token", { token }, "json", setErrorMessage, navigate);
      return response.valid;
    } catch (error) {
      console.error("Token validation failed:", error);
      handleLogout(); // Token validation failed, logout
      return false;
    }
  };

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      // console.log("Initializing auth...");
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");

      if (storedToken && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser); // Try parsing user data
          // console.log("Token and user found, validating...");
          const isValid = await validateToken(storedToken);
          if (isValid) {
            setUser(parsedUser); // Set user data if valid
            setLoading(false); // Stop loading when user is fetched
          } else {
            handleLogout(); // Handle logout if token is invalid
          }
        } catch (error) {
          console.error("Error parsing user data from localStorage:", error);
          setLoading(false); // Stop loading on error
          handleLogout(); // Ensure the user is logged out on parsing error
        }
      } else {
        // console.log("No token or user found.");
        setLoading(false); // Stop loading if no user or token found
      }
    };

    initializeAuth();
  }, []);

  // Handle user login
  const login = (userData, token) => {
    setUser(userData); // Update user context
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("token", token);
    navigate(userData.role === "applicant" ? "/applicant" : "/dashboard");
    setLoading(false); // Stop loading once the user is set
    console.log("User logged in:", userData);
  };

  // Handle user logout
  const handleLogout = () => {
    console.log("Logging out...");
    setUser(null);
    setPermissions([]); // Reset permissions on logout
    localStorage.clear(); // Clear localStorage
    navigate("/"); // Navigate to home page
    clearTimeout(); // Clear any timeouts
    console.log("User logged out.");
  };

  return (
    <AuthContext.Provider value={{ user, loginType, login, setLoginType, handleLogout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
